import React, { useState, useMemo, useCallback, useRef } from 'react';
import CoordinateGrid, { GridPoint } from './CoordinateGrid';
import LessonHeader, { MasteryQuestion } from './LessonHeader';
import ExerciseFooter, { FooterState } from './ExerciseFooter';

// ============ TYPES ============

type LessonNodeType = 'intro' | 'question' | 'explanatory' | 'outro' | 'skill-check-intro' | 'complete';

interface LessonNode {
  id: string;
  type: LessonNodeType;
  title?: string;
  text?: string;
  bottomText?: string;
  questionText?: string;
  gridSize?: number;
  gridRange?: number;
  targetCoordinates?: { x: number; y: number };
  displayPoints?: GridPoint[];
  explanation?: string;
  xpReward?: number;
}

// ============ LESSON DATA ============

const LESSON_NODES: LessonNode[] = [
  // Question 1
  {
    id: 'q1',
    type: 'question',
    questionText: 'Place a point at the coordinates (2, 1).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: 2, y: 1 },
    explanation: 'The coordinates (2, 1) refer to a point that is 2 steps right and 1 step up from the origin.',
    xpReward: 15,
  },
  // Question 2
  {
    id: 'q2',
    type: 'question',
    questionText: 'Now place a point at the coordinates (-1, 3).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: -1, y: 3 },
    explanation: 'The coordinates (-1, 3) refer to a point that is 1 step left and 3 steps up from the origin.',
    xpReward: 15,
  },
  // Explanatory node
  {
    id: 'exp1',
    type: 'explanatory',
    text: 'Great work! You\'ve learned how to plot points using coordinates.',
    bottomText: 'Remember: the first number (x) tells you left/right, and the second number (y) tells you up/down.',
  },
  // Question 3
  {
    id: 'q3',
    type: 'question',
    questionText: 'Place a point at the coordinates (4, -2).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: 4, y: -2 },
    explanation: 'The coordinates (4, -2) mean 4 steps right and 2 steps down (negative y) from the origin.',
    xpReward: 15,
  },
  // Question 4
  {
    id: 'q4',
    type: 'question',
    questionText: 'Place a point at the coordinates (-3, -2).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: -3, y: -2 },
    explanation: 'The coordinates (-3, -2) mean 3 steps left and 2 steps down from the origin.',
    xpReward: 20,
  },
  // Skill Check Intro
  {
    id: 'skill-check-intro',
    type: 'skill-check-intro',
    title: 'Skill Check',
  },
  // Skill Check Q1
  {
    id: 'skill-q1',
    type: 'question',
    questionText: 'Place a point at the coordinates (0, 4).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: 0, y: 4 },
    explanation: 'The coordinates (0, 4) are on the y-axis, 4 steps up from the origin.',
    xpReward: 15,
  },
  // Skill Check Q2
  {
    id: 'skill-q2',
    type: 'question',
    questionText: 'Place a point at the coordinates (-4, 1).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: -4, y: 1 },
    explanation: 'The coordinates (-4, 1) mean 4 steps left and 1 step up.',
    xpReward: 15,
  },
  // Skill Check Q3
  {
    id: 'skill-q3',
    type: 'question',
    questionText: 'Place a point at the coordinates (3, -4).',
    gridSize: 340,
    gridRange: 5,
    targetCoordinates: { x: 3, y: -4 },
    explanation: 'The coordinates (3, -4) mean 3 steps right and 4 steps down.',
    xpReward: 15,
  },
  // Complete
  {
    id: 'complete',
    type: 'complete',
    title: 'Lesson Complete!',
    text: 'You can re-earn XP from this lesson by redoing it a day after completion.',
  },
];

const SKILL_CHECK_IDS = ['skill-q1', 'skill-q2', 'skill-q3'];

// ============ COMPONENT ============

interface StoryLessonProps {
  onClose?: () => void;
}

const StoryLesson: React.FC<StoryLessonProps> = ({ onClose }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Current position in lesson
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [visibleNodeIndices, setVisibleNodeIndices] = useState<number[]>([0]);

  // Point positions for each question
  const [pointPositions, setPointPositions] = useState<Record<string, { x: number; y: number }>>({});

  // Completed questions
  const [completedQuestions, setCompletedQuestions] = useState<Set<string>>(new Set());

  // Incorrect attempts
  const [incorrectAttempts, setIncorrectAttempts] = useState<Record<string, number>>({});

  // Streak and XP
  const [streakCount, setStreakCount] = useState(0);
  const [totalXP, setTotalXP] = useState(0);

  // Skill check mode
  const [inSkillCheckMode, setInSkillCheckMode] = useState(false);

  // Why expanded states
  const [whyExpanded, setWhyExpanded] = useState<Record<string, boolean>>({});

  // Point visual states
  const [pointStates, setPointStates] = useState<Record<string, 'default' | 'correct' | 'incorrect'>>({});

  // AI Buddy state
  const [showBuddyChat, setShowBuddyChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'buddy' | 'user'; text: string }>>([]);
  const [chatInput, setChatInput] = useState('');

  const currentNode = LESSON_NODES[currentNodeIndex];

  // Progress (0 to 1)
  const progress = useMemo(() => {
    const skillCheckIntroIndex = LESSON_NODES.findIndex(n => n.type === 'skill-check-intro');
    if (skillCheckIntroIndex === -1) return 0;
    if (currentNodeIndex >= skillCheckIntroIndex) return 1;
    return currentNodeIndex / skillCheckIntroIndex;
  }, [currentNodeIndex]);

  // Mastery questions (skill check)
  const masteryQuestions = useMemo<MasteryQuestion[]>(() => {
    return SKILL_CHECK_IDS.map(id => {
      if (completedQuestions.has(id)) {
        return { state: 'correct' as const };
      } else if (LESSON_NODES[currentNodeIndex]?.id === id) {
        return { state: 'current' as const };
      }
      return { state: 'pending' as const };
    });
  }, [currentNodeIndex, completedQuestions]);

  // Footer state
  const footerState = useMemo<FooterState>(() => {
    if (!currentNode || currentNode.type !== 'question') return 'default';
    if (completedQuestions.has(currentNode.id)) return 'correct';
    if ((incorrectAttempts[currentNode.id] || 0) > 0) return 'incorrect';
    return 'default';
  }, [currentNode, completedQuestions, incorrectAttempts]);

  // Check if has selection
  const hasSelection = useMemo(() => {
    if (!currentNode || currentNode.type !== 'question') return true;
    return pointPositions[currentNode.id] !== undefined;
  }, [currentNode, pointPositions]);

  // Check answer
  const checkAnswer = useCallback((node: LessonNode): boolean => {
    const userPos = pointPositions[node.id];
    if (!userPos || !node.targetCoordinates) return false;
    return userPos.x === node.targetCoordinates.x && userPos.y === node.targetCoordinates.y;
  }, [pointPositions]);

  // Handle point placement/move
  const handlePointChange = useCallback((nodeId: string, pos: { x: number; y: number }) => {
    setPointPositions(prev => ({ ...prev, [nodeId]: pos }));
  }, []);

  // Scroll to node
  const scrollToNode = useCallback((index: number) => {
    setTimeout(() => {
      const element = document.getElementById(`node-${index}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // Advance to next node
  const advanceToNextNode = useCallback(() => {
    const nextIdx = currentNodeIndex + 1;
    if (nextIdx >= LESSON_NODES.length) return;

    const nextNode = LESSON_NODES[nextIdx];

    if (nextNode.type === 'skill-check-intro') {
      setInSkillCheckMode(true);
    }

    if (inSkillCheckMode || nextNode.type === 'skill-check-intro') {
      setVisibleNodeIndices([nextIdx]);
    } else {
      setVisibleNodeIndices(prev => [...prev, nextIdx]);
    }

    setCurrentNodeIndex(nextIdx);
    scrollToNode(nextIdx);

    // Reset chat for new question
    setChatMessages([]);
    setShowBuddyChat(false);
  }, [currentNodeIndex, inSkillCheckMode, scrollToNode]);

  // Handle continue
  const handleContinue = useCallback(() => {
    if (!currentNode) return;

    // For complete node, close the modal
    if (currentNode.type === 'complete') {
      onClose?.();
      return;
    }

    // For question nodes that aren't completed, check answer
    if (currentNode.type === 'question' && !completedQuestions.has(currentNode.id)) {
      const isCorrect = checkAnswer(currentNode);

      setPointStates(prev => ({
        ...prev,
        [currentNode.id]: isCorrect ? 'correct' : 'incorrect'
      }));

      if (isCorrect) {
        setCompletedQuestions(prev => new Set([...prev, currentNode.id]));
        setTotalXP(prev => prev + (currentNode.xpReward || 15));
        setStreakCount(prev => prev + 1);
        setIncorrectAttempts(prev => ({ ...prev, [currentNode.id]: 0 }));
      } else {
        setIncorrectAttempts(prev => ({
          ...prev,
          [currentNode.id]: (prev[currentNode.id] || 0) + 1
        }));
        setStreakCount(0);
      }
      return;
    }

    // Otherwise advance
    advanceToNextNode();
  }, [currentNode, completedQuestions, checkAnswer, advanceToNextNode, onClose]);

  // Handle try again
  const handleTryAgain = useCallback(() => {
    if (!currentNode) return;
    setIncorrectAttempts(prev => ({ ...prev, [currentNode.id]: 0 }));
    setPointPositions(prev => {
      const newPos = { ...prev };
      delete newPos[currentNode.id];
      return newPos;
    });
    setPointStates(prev => {
      const newStates = { ...prev };
      delete newStates[currentNode.id];
      return newStates;
    });
  }, [currentNode]);

  // Toggle why expanded
  const toggleWhyExpanded = useCallback((nodeId: string) => {
    setWhyExpanded(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  }, []);

  // Generate hint
  const generateHint = useCallback((node: LessonNode, hintLevel: number): string => {
    const target = node.targetCoordinates;
    if (!target) return "Try placing a point on the grid!";

    const hints = [
      `Remember: the first number (x) tells you how far to go left or right. The second number (y) tells you how far to go up or down.`,
      `Start at the origin (0,0) where the axes cross. Move ${target.x >= 0 ? 'right' : 'left'} ${Math.abs(target.x)} steps, then ${target.y >= 0 ? 'up' : 'down'} ${Math.abs(target.y)} steps.`,
      `The point should be at (${target.x}, ${target.y}).`,
    ];
    return hints[Math.min(hintLevel, hints.length - 1)];
  }, []);

  // Handle buddy chat
  const handleBuddyMessage = useCallback((message: string) => {
    const lowerMessage = message.toLowerCase();
    setChatMessages(prev => [...prev, { role: 'user', text: message }]);

    const hintCount = chatMessages.filter(m => m.role === 'buddy').length;

    let response: string;
    if (lowerMessage.includes('hint') || lowerMessage.includes('help') || lowerMessage.includes('stuck')) {
      response = generateHint(currentNode, hintCount);
    } else if (lowerMessage.includes('explain') || lowerMessage.includes('how')) {
      response = `This is a coordinate geometry question. ${generateHint(currentNode, 0)}`;
    } else {
      response = `I'm here to help with coordinate geometry! Ask me for a hint if you're stuck.`;
    }

    setTimeout(() => {
      setChatMessages(prev => [...prev, { role: 'buddy', text: response }]);
    }, 500);
  }, [currentNode, chatMessages, generateHint]);

  // Render a single node
  const renderNode = (node: LessonNode, index: number) => {
    const isCompleted = completedQuestions.has(node.id);
    const isCurrent = index === currentNodeIndex;

    // Question node
    if (node.type === 'question') {
      const userPos = pointPositions[node.id];
      const pointState = pointStates[node.id];

      const points: GridPoint[] = userPos ? [{
        id: 'user',
        x: userPos.x,
        y: userPos.y,
        draggable: !isCompleted,
        showLabel: true,
        color: pointState === 'correct' ? '#10b981' : pointState === 'incorrect' ? '#ef4444' : '#0ea5e9',
        state: pointState,
      }] : [];

      return (
        <div key={node.id} id={`node-${index}`} className="py-6">
          <p className="text-lg font-bold mb-4 text-center" style={{ color: '#e2e8f0' }}>
            {node.questionText}
          </p>

          <div className="flex justify-center mb-4">
            <CoordinateGrid
              size={node.gridSize || 340}
              gridRange={node.gridRange || 5}
              points={points}
              disabled={isCompleted}
              allowPlacement={!isCompleted && !userPos}
              onPointMove={(_id, pos) => handlePointChange(node.id, pos)}
              onGridClick={(pos) => handlePointChange(node.id, pos)}
            />
          </div>

          {/* Why button (after completion in story mode) */}
          {isCompleted && !inSkillCheckMode && node.explanation && (
            <div className="mt-4 mb-6">
              <button
                onClick={() => toggleWhyExpanded(node.id)}
                className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg transition-colors"
                style={{ background: 'rgba(14, 165, 233, 0.1)', color: '#0ea5e9' }}
              >
                Why?
                <svg
                  className={`w-4 h-4 transition-transform ${whyExpanded[node.id] ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              {whyExpanded[node.id] && (
                <div className="mt-3 p-4 rounded-xl" style={{ background: 'rgba(30, 41, 59, 0.8)', color: '#94a3b8' }}>
                  {node.explanation}
                </div>
              )}
            </div>
          )}

          {/* Divider after completed questions */}
          {isCompleted && !isCurrent && (
            <div className="my-6 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          )}
        </div>
      );
    }

    // Explanatory node
    if (node.type === 'explanatory') {
      return (
        <div key={node.id} id={`node-${index}`} className="py-6">
          <div className="p-6 rounded-xl" style={{ background: 'rgba(30, 41, 59, 0.6)' }}>
            <p className="text-lg mb-4" style={{ color: '#94a3b8' }}>{node.text}</p>
            {node.bottomText && (
              <p className="text-lg" style={{ color: '#94a3b8' }}>{node.bottomText}</p>
            )}
          </div>
        </div>
      );
    }

    // Skill check intro
    if (node.type === 'skill-check-intro') {
      return (
        <div key={node.id} id={`node-${index}`} className="py-12 text-center">
          <div
            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)' }}
          >
            <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h2 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>{node.title}</h2>
        </div>
      );
    }

    // Complete node
    if (node.type === 'complete') {
      return (
        <div key={node.id} id={`node-${index}`} className="py-12 text-center">
          <div className="relative mb-8 inline-block">
            <div
              className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3"
              style={{ background: 'linear-gradient(180deg, #0ea5e9 0%, #3b82f6 100%)' }}
            >
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L9.5 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5 6-4.5h-7.5L12 2z"/>
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Lesson</h1>
          <h1 className="text-3xl font-bold mb-6" style={{ color: '#e2e8f0' }}>Complete!</h1>
          <p className="text-sm mb-2" style={{ color: '#64748b' }}>TOTAL XP</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-5xl font-bold" style={{ color: '#e2e8f0' }}>{totalXP}</span>
            <svg className="w-8 h-8" style={{ color: '#0ea5e9' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L9.5 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5 6-4.5h-7.5L12 2z"/>
            </svg>
          </div>
          {node.text && (
            <p className="text-sm mt-6" style={{ color: '#94a3b8' }}>{node.text}</p>
          )}
        </div>
      );
    }

    return null;
  };

  // Visible nodes
  const visibleNodes = useMemo(() => {
    return visibleNodeIndices.map(i => ({ index: i, node: LESSON_NODES[i] }));
  }, [visibleNodeIndices]);

  // Button text
  const buttonText = useMemo(() => {
    if (!currentNode) return 'Continue';
    if (currentNode.type === 'complete') return 'Finish';
    if (currentNode.type === 'question' && !completedQuestions.has(currentNode.id)) return 'Check';
    return 'Continue';
  }, [currentNode, completedQuestions]);

  // Is last question
  const isLastQuestion = useMemo(() => {
    const completeIdx = LESSON_NODES.findIndex(n => n.type === 'complete');
    return currentNodeIndex === completeIdx - 1;
  }, [currentNodeIndex]);

  return (
    <div
      className="h-full flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        minHeight: '600px',
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-50">
        <LessonHeader
          progress={progress}
          masteryQuestions={inSkillCheckMode ? masteryQuestions : []}
          streakCount={streakCount}
          onClose={onClose}
          mode="story"
        />
      </div>

      {/* Content - scrollable */}
      <div ref={contentRef} className="flex-1 overflow-y-auto pb-52">
        <div className="max-w-2xl mx-auto px-6">
          {visibleNodes.map(({ index, node }) => renderNode(node, index))}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40">
        <ExerciseFooter
          state={footerState}
          buttonText={buttonText}
          buttonEnabled={hasSelection}
          xpAmount={currentNode?.xpReward || 15}
          isLastQuestion={isLastQuestion}
          onContinue={handleContinue}
          onTryAgain={handleTryAgain}
          onWhy={() => toggleWhyExpanded(currentNode?.id || '')}
        />
      </div>

      {/* AI Buddy Button */}
      {!showBuddyChat && currentNode?.type === 'question' && !completedQuestions.has(currentNode.id) && (
        <button
          onClick={() => {
            setShowBuddyChat(true);
            if (chatMessages.length === 0) {
              setChatMessages([{
                role: 'buddy',
                text: `Hi! I'm here to help with coordinate geometry. Click anywhere on the grid to place your point. Ask me for a hint if you get stuck!`
              }]);
            }
          }}
          className="fixed bottom-24 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 z-50"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)' }}
        >
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* AI Buddy Chat Panel */}
      {showBuddyChat && (
        <div
          className="fixed bottom-24 right-6 w-80 rounded-2xl shadow-2xl overflow-hidden z-50"
          style={{ background: 'rgba(30, 41, 59, 0.98)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          {/* Chat Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-lg">🤖</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Study Buddy</p>
                <p className="text-xs text-white/70">Here to help!</p>
              </div>
            </div>
            <button
              onClick={() => setShowBuddyChat(false)}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Chat Messages */}
          <div className="h-64 overflow-y-auto p-4 space-y-3">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-md'
                      : 'bg-slate-700 text-slate-200 rounded-bl-md'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && chatInput.trim()) {
                    handleBuddyMessage(chatInput.trim());
                    setChatInput('');
                  }
                }}
                placeholder="Ask for a hint..."
                className="flex-1 px-3 py-2 rounded-xl text-sm bg-slate-700 text-white placeholder-slate-400 border border-slate-600 focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={() => {
                  if (chatInput.trim()) {
                    handleBuddyMessage(chatInput.trim());
                    setChatInput('');
                  }
                }}
                className="w-10 h-10 rounded-xl bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            {/* Quick hint buttons */}
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleBuddyMessage('Give me a hint')}
                className="px-3 py-1 rounded-full text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
              >
                Hint please
              </button>
              <button
                onClick={() => handleBuddyMessage('How do coordinates work?')}
                className="px-3 py-1 rounded-full text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
              >
                How to solve?
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryLesson;
